import androidx.compose.runtime.Composable
import androidx.compose.runtime.ProvidableCompositionLocal
import androidx.compose.runtime.compositionLocalOf
import com.varabyte.kobweb.core.AppGlobals
import com.varabyte.kobweb.navigation.BasePath
import com.varabyte.kobweb.navigation.Router
import com.varabyte.kobweb.navigation.UpdateHistoryMode
import com.varabyte.kobweb.navigation.remove
import com.varabyte.kobweb.silk.defer.DeferringHost
import kotlin.Any
import kotlin.Unit
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.web.renderComposable
import pl.michal_cyran.portfolio.components.layouts.MarkdownLayout as _pl_michal_cyran_portfolio_components_layouts_MarkdownLayout
import pl.michal_cyran.portfolio.components.layouts.PageLayout as _pl_michal_cyran_portfolio_components_layouts_PageLayout
import pl.michal_cyran.portfolio.pages.AboutPage as _pl_michal_cyran_portfolio_pages_AboutPage
import pl.michal_cyran.portfolio.pages.AchievementsPage as _pl_michal_cyran_portfolio_pages_AchievementsPage
import pl.michal_cyran.portfolio.pages.EducationPage as _pl_michal_cyran_portfolio_pages_EducationPage
import pl.michal_cyran.portfolio.pages.ExperiencePage as _pl_michal_cyran_portfolio_pages_ExperiencePage
import pl.michal_cyran.portfolio.pages.IndexPage as _pl_michal_cyran_portfolio_pages_IndexPage
import pl.michal_cyran.portfolio.pages.ProjectDescriptionPage as _pl_michal_cyran_portfolio_pages_ProjectDescriptionPage
import pl.michal_cyran.portfolio.pages.ProjectsPage as _pl_michal_cyran_portfolio_pages_ProjectsPage
import pl.michal_cyran.portfolio.pages.SkillsPage as _pl_michal_cyran_portfolio_pages_SkillsPage

private val LayoutScopeLocal: ProvidableCompositionLocal<Any?> = compositionLocalOf<Any?> { null }

@Composable
private fun <T : Any> currentLayoutScope(): T {
    @Suppress("UNCHECKED_CAST")
    return LayoutScopeLocal.current as? T
        ?: error("Unexpected between Page/Layout scope and the parent Layout which it is providing it. Please report this issue to the Kobweb developers.")
}

@Composable
private fun <T : Any> provideLayoutScope(layoutScope: T, content: @Composable () -> Unit) {
    androidx.compose.runtime.CompositionLocalProvider(LayoutScopeLocal provides layoutScope, content = content)
}

public fun main() {
    AppGlobals.initialize(mapOf("title" to "Portfolio"))
    BasePath.set("")
    val router = Router()
    com.varabyte.kobweb.core.init.initKobweb(router) { ctx ->
        ctx.router.registerLayout(
            "pl.michal_cyran.portfolio.components.layouts.MarkdownLayout",
            parentLayoutId = "pl.michal_cyran.portfolio.components.layouts.PageLayout",
            initRouteMethod = { ctx ->
                pl.michal_cyran.portfolio.components.layouts.initMarkdownLayout(ctx)
            },
        ) { pageCtx, pageMethod -> 
            _pl_michal_cyran_portfolio_components_layouts_MarkdownLayout {
                pageMethod(pageCtx)
            }
        }
        ctx.router.registerLayout(
            "pl.michal_cyran.portfolio.components.layouts.PageLayout",
        ) { pageCtx, pageMethod -> 
            _pl_michal_cyran_portfolio_components_layouts_PageLayout(pageCtx) {
                provideLayoutScope(this) {
                    pageMethod(pageCtx)
                }
            }
        }
        ctx.router.register(
            "/",
            layoutId = "pl.michal_cyran.portfolio.components.layouts.PageLayout",
            initRouteMethod = { ctx ->
                pl.michal_cyran.portfolio.pages.initMainPage(ctx)
            },
        ) { pageCtx -> 
            _pl_michal_cyran_portfolio_pages_IndexPage()
        }
        ctx.router.register(
            "/about",
            layoutId = "pl.michal_cyran.portfolio.components.layouts.MarkdownLayout",
            initRouteMethod = { ctx ->
                pl.michal_cyran.portfolio.pages.initAboutPage(ctx)
            },
        ) { pageCtx -> 
            _pl_michal_cyran_portfolio_pages_AboutPage()
        }
        ctx.router.register(
            "/achievements",
            layoutId = "pl.michal_cyran.portfolio.components.layouts.PageLayout",
            initRouteMethod = { ctx ->
                pl.michal_cyran.portfolio.pages.initAchievementsPage(ctx)
            },
        ) { pageCtx -> 
            _pl_michal_cyran_portfolio_pages_AchievementsPage()
        }
        ctx.router.register(
            "/education",
            layoutId = "pl.michal_cyran.portfolio.components.layouts.PageLayout",
            initRouteMethod = { ctx ->
                pl.michal_cyran.portfolio.pages.initEducationPage(ctx)
            },
        ) { pageCtx -> 
            _pl_michal_cyran_portfolio_pages_EducationPage()
        }
        ctx.router.register(
            "/experience",
            layoutId = "pl.michal_cyran.portfolio.components.layouts.PageLayout",
            initRouteMethod = { ctx ->
                pl.michal_cyran.portfolio.pages.initExperiencePage(ctx)
            },
        ) { pageCtx -> 
            _pl_michal_cyran_portfolio_pages_ExperiencePage()
        }
        ctx.router.register(
            "/project-description",
            layoutId = "pl.michal_cyran.portfolio.components.layouts.PageLayout",
            initRouteMethod = { ctx ->
                pl.michal_cyran.portfolio.pages.initProjectDescriptionPage(ctx)
            },
        ) { pageCtx -> 
            _pl_michal_cyran_portfolio_pages_ProjectDescriptionPage(pageCtx)
        }
        ctx.router.register(
            "/projects",
            layoutId = "pl.michal_cyran.portfolio.components.layouts.PageLayout",
            initRouteMethod = { ctx ->
                pl.michal_cyran.portfolio.pages.initProjectsPage(ctx)
            },
        ) { pageCtx -> 
            _pl_michal_cyran_portfolio_pages_ProjectsPage(pageCtx)
        }
        ctx.router.register(
            "/skills",
            layoutId = "pl.michal_cyran.portfolio.components.layouts.PageLayout",
            initRouteMethod = { ctx ->
                pl.michal_cyran.portfolio.pages.initSkillsPage(ctx)
            },
        ) { pageCtx -> 
            _pl_michal_cyran_portfolio_pages_SkillsPage()
        }

    }
    router.addRouteInterceptor {
        path = path.removeSuffix(".html").removeSuffix(".htm")
    }

    com.varabyte.kobweb.silk.init.additionalSilkInitialization = { ctx ->
        com.varabyte.kobweb.silk.init.initSilkWidgets(ctx)
        com.varabyte.kobweb.silk.init.initSilkWidgetsKobweb(ctx)
        ctx.theme.registerStyle("all", pl.michal_cyran.portfolio.AllStyle)
        ctx.theme.registerStyle("headline-text", pl.michal_cyran.portfolio.HeadlineTextStyle)
        ctx.theme.registerStyle("subheadline-text", pl.michal_cyran.portfolio.SubheadlineTextStyle)
        ctx.theme.registerStyle("tech-fact-label", pl.michal_cyran.portfolio.projects.presentation.description.TechFactLabelStyle)
        ctx.theme.registerStyle("logo", pl.michal_cyran.portfolio.projects.presentation.description.LogoStyle)
        ctx.theme.registerStyle("skill-logo", pl.michal_cyran.portfolio.projects.presentation.description.SkillLogoStyle)
        ctx.theme.registerStyle("link-button", pl.michal_cyran.portfolio.projects.presentation.description.LinkButtonStyle)
        ctx.theme.registerStyle("link-icon", pl.michal_cyran.portfolio.projects.presentation.description.LinkIconStyle)
        ctx.theme.registerStyle("screenshot-card", pl.michal_cyran.portfolio.projects.presentation.description.ScreenshotCardStyle)
        ctx.theme.registerStyle("chip-container", pl.michal_cyran.portfolio.core.presentation.ChipContainerStyle)
        ctx.theme.registerStyle("chip", pl.michal_cyran.portfolio.core.presentation.ChipStyle)
        ctx.theme.registerStyle("image-box", pl.michal_cyran.portfolio.core.presentation.styles.ImageBoxStyle)
        ctx.theme.registerStyle("list", pl.michal_cyran.portfolio.core.presentation.styles.ListStyle)
        ctx.theme.registerStyle("hero", pl.michal_cyran.portfolio.core.presentation.styles.HeroStyle)
        ctx.theme.registerStyle("hero-label", pl.michal_cyran.portfolio.core.presentation.styles.HeroLabelStyle)
        ctx.theme.registerStyle("hero-title", pl.michal_cyran.portfolio.core.presentation.styles.HeroTitleStyle)
        ctx.theme.registerStyle("hero-desc", pl.michal_cyran.portfolio.core.presentation.styles.HeroDescStyle)
        ctx.theme.registerStyle("hero-actions", pl.michal_cyran.portfolio.core.presentation.styles.HeroActionsStyle)
        ctx.theme.registerStyle("hero-right", pl.michal_cyran.portfolio.core.presentation.styles.HeroRightStyle)
        ctx.theme.registerStyle("stack-panel", pl.michal_cyran.portfolio.core.presentation.styles.StackPanelStyle)
        ctx.theme.registerStyle("stack-title", pl.michal_cyran.portfolio.core.presentation.styles.StackTitleStyle)
        ctx.theme.registerStyle("tech-list", pl.michal_cyran.portfolio.core.presentation.styles.TechListStyle)
        ctx.theme.registerStyle("tech-item", pl.michal_cyran.portfolio.core.presentation.styles.TechItemStyle)
        ctx.theme.registerStyle("tech-dot", pl.michal_cyran.portfolio.core.presentation.styles.TechDotStyle)
        ctx.theme.registerStyle("tech-name", pl.michal_cyran.portfolio.core.presentation.styles.TechNameStyle)
        ctx.theme.registerStyle("tech-role", pl.michal_cyran.portfolio.core.presentation.styles.TechRoleStyle)
        ctx.theme.registerStyle("education-card", pl.michal_cyran.portfolio.core.presentation.styles.EducationCardStyle)
        ctx.theme.registerStyle("page-header", pl.michal_cyran.portfolio.core.presentation.styles.PageHeaderStyle)
        ctx.theme.registerStyle("page-subtitle", pl.michal_cyran.portfolio.core.presentation.styles.PageSubtitleStyle)
        ctx.theme.registerStyle("school-logo", pl.michal_cyran.portfolio.core.presentation.styles.SchoolLogoStyle)
        ctx.theme.registerStyle("body", pl.michal_cyran.portfolio.core.presentation.BodyStyle)
        ctx.theme.registerStyle("container", pl.michal_cyran.portfolio.core.presentation.ContainerStyle)
        ctx.theme.registerStyle("cards-grid", pl.michal_cyran.portfolio.core.presentation.CardsGridStyle)
        ctx.theme.registerStyle("card", pl.michal_cyran.portfolio.core.presentation.CardStyle)
        ctx.theme.registerStyle("card-blur", pl.michal_cyran.portfolio.core.presentation.CardBlurStyle)
        ctx.theme.registerStyle("card-content", pl.michal_cyran.portfolio.core.presentation.CardContentStyle)
        ctx.theme.registerStyle("icon-wrapper", pl.michal_cyran.portfolio.core.presentation.IconWrapperStyle)
        ctx.theme.registerStyle("icon", pl.michal_cyran.portfolio.core.presentation.IconStyle)
        ctx.theme.registerStyle("card-title", pl.michal_cyran.portfolio.core.presentation.CardTitleStyle)
        ctx.theme.registerStyle("card-description", pl.michal_cyran.portfolio.core.presentation.CardDescriptionStyle)
        ctx.theme.registerStyle("card-link", pl.michal_cyran.portfolio.core.presentation.CardLinkStyle)
        ctx.theme.registerStyle("arrow-icon", pl.michal_cyran.portfolio.core.presentation.ArrowIconStyle)
        ctx.theme.registerStyle("box", pl.michal_cyran.portfolio.skils.presentation.BoxStyle)
        ctx.theme.registerStyle("background-container", pl.michal_cyran.portfolio.colors.backgroundContainer)
        ctx.theme.registerStyle("secondary-container", pl.michal_cyran.portfolio.colors.secondaryContainer)
        ctx.theme.registerStyle("page-content", pl.michal_cyran.portfolio.components.layouts.PageContentStyle)
        ctx.theme.registerStyle("markdown", pl.michal_cyran.portfolio.components.layouts.MarkdownStyle)
        ctx.theme.registerStyle("footer", pl.michal_cyran.portfolio.components.sections.FooterStyle)
        ctx.theme.registerStyle("nav-header", pl.michal_cyran.portfolio.components.sections.NavHeaderStyle)
        ctx.theme.registerVariant("-circle", pl.michal_cyran.portfolio.CircleButtonVariant
        )
        ctx.theme.registerVariant("-uncolored", pl.michal_cyran.portfolio.UncoloredButtonVariant
        )
        ctx.theme.registerKeyframes("fade-in-down", pl.michal_cyran.portfolio.core.presentation.styles.FadeInDown)
        ctx.theme.registerKeyframes("fade-in-up", pl.michal_cyran.portfolio.core.presentation.styles.FadeInUp)
        ctx.theme.registerKeyframes("side-menu-slide-in", pl.michal_cyran.portfolio.components.sections.SideMenuSlideInAnim)
        pl.michal_cyran.portfolio.initSiteStyles(ctx)
        pl.michal_cyran.portfolio.initColorMode(ctx)
        pl.michal_cyran.portfolio.initStyles(ctx)
        pl.michal_cyran.portfolio.initTheme(ctx)
    }

    // Dedup any leading slashes after removing the origin, just in case someone typed
    // something like `https://site.com//about` by accident. If we pass `//about` into
    // `tryRoutingTo`, Kobweb will reject it as a protocol-relative URL; instead, we
    // want it to navigate to `/about`
    router.tryRoutingTo(BasePath.remove("/" + window.location.href.removePrefix(window.origin).trimStart('/')), UpdateHistoryMode.REPLACE)

    // For SEO, we may bake the contents of a page in at build time. However, we will
    // overwrite them the first time we render this page with their composable, dynamic
    // versions. Think of this as poor man's hydration :)
    // See also: https://en.wikipedia.org/wiki/Hydration_(web_development)
    val root = document.getElementById("_kobweb-root")!!
    while (root.firstChild != null) { root.removeChild(root.firstChild!!) }

    renderComposable(rootElementId = "_kobweb-root") {
        pl.michal_cyran.portfolio.AppEntry {
            router.renderActivePage { DeferringHost { it() } }
        }
    }
}
